import classNames from 'classnames'
import React from 'react'

import { SubscriptionModelPermissions } from '../../../common/types.js'
import { SubscriptionProduct } from '../../models/subscription.js'
import { Button, ButtonProps } from '../button/button.js'
import { FlexLogo } from '../flex-logo/flex-logo.js'
import { Separator } from '../separator/separator.js'

export interface ProductProps {
    sku?: string
    title?: string
    nickname?: string | null
    content: React.ReactNode
    image?: JSX.Element
    className?: string
    description?: React.ReactNode
    variant?: 'big'
    meta?: {
        product: SubscriptionProduct
        subscriptionUuid: string
        uuid: string
        eligibleDate?: string
        activeDate?: string
        permissions?: SubscriptionModelPermissions
    }
    footer?: {
        image?: JSX.Element
        title: JSX.Element | string
        description: JSX.Element | string
        additionalDescription?: JSX.Element | string
        disclaimer?: JSX.Element | string
        actions: ButtonProps[]
    }
    disclaimers?: React.ReactNode[]
}

export const Product = (props: ProductProps): JSX.Element => {
    const { content, description, nickname, image, title, footer } = props

    const className: string = classNames(
        'product',
        props.variant && `product--variant-${props.variant}`,
        props.className,
    )

    return (
        <div className={className}>
            <div className="product__inner">
                {image}
                <div className="product__info">
                    {title && (
                        <h3>
                            {title} {nickname && <span className="text-small">({nickname})</span>}
                        </h3>
                    )}
                    <div className="product__content text-small">{content}</div>
                    {description && <div className="product__description text-small">{description}</div>}
                </div>
            </div>
            {footer && (
                <div className="product__footer">
                    <Separator variant="slim" />
                    <div className="product__footer-inner">
                        <div className="product__footer-image">
                            <FlexLogo width={40} />
                        </div>
                        <div className="product__footer-content">
                            <div className="product__footer-body">
                                <div className="product__footer-title">{footer.title}</div>
                                <div className="product__footer-inner-content text-tiny">
                                    <span className="product__footer-description text-light">{footer.description}</span>
                                    {footer.additionalDescription && (
                                        <span className="product__footer-additional-description text-tiny">
                                            {footer.additionalDescription}
                                        </span>
                                    )}
                                    {footer.disclaimer && (
                                        <div className="product__footer-additional-disclaimer text-error">
                                            {footer.disclaimer}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="product__footer-actions">
                                {footer.actions.map((action: ButtonProps, index: number) => {
                                    return <Button key={index} {...action} variant="secondary" />
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!!props.disclaimers?.length && (
                <div className="product__disclaimers">
                    <ul className="product__disclaimers-list">
                        {props.disclaimers.map((disclaimer, index) => (
                            <li className="text-tiny" key={title + 'disc-' + index}>
                                {disclaimer}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
